import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useBoardTheme } from '@exports/hooks/useBoardTheme';
import { getLocalStorage } from '@libs/storage';
import { Tab, Tabs } from '@ui/components/Tabs';

import { useTournamentGames } from '@features/tournament/hooks/useTournamentGames';
import { useTournamentCtx } from '@features/tournament/providers/TournamentProvider';
import { KnockoutRoundName } from '@features/tournament/ui/components/KnockoutRoundName';
import { PlayerShieldQuickPeekWidget } from '@features/user/ui/PlayerShieldQuickPeek/PlayerShieldQuickPeekWidget';

import { GAMES_FILTER_TABS, GamesFilter } from './constants';
import { TournamentGames } from './TournamentGames';

/*
    ____,-------------------------------,____
    \   |            Виджет             |   /
    /___|-------------------------------|___\
*/

export function TournamentGamesWidget() {
  const { boardTheme } = useBoardTheme();
  const navigate = useNavigate();

  const { gamesHidden, onGamesToggle } = useGamesToggle();
  const [filter, setFilter] = useState<GamesFilter>('all');

  const { state, selectRound } = useTournamentCtx();
  const { tournament, clubId, tournamentId, visibleRounds, selectedRound } = state;

  const isKnockout = tournament?.system === 'Knockout';
  const isRoundFinished = selectedRound?.status === 'Finished';

  const roundRender = useCallback(
    (roundNumber: number) => {
      if (!visibleRounds) return roundNumber;

      const r = visibleRounds.find((r) => r.roundNumber === roundNumber);
      if (!r) return roundNumber;

      if (isKnockout) {
        return <KnockoutRoundName roundType="WinnerBranch" roundName={r.roundName} />;
      }

      return r.roundNumber;
    },
    [visibleRounds, isKnockout],
  );

  const { games, prevGames } = useTournamentGames(tournamentId, selectedRound, {
    enabled: !gamesHidden,
  });

  const source = games ?? prevGames;
  const hasFinishedGames = source?.some((g) => g.winner !== undefined) ?? false;

  const filterTabs: Tab<GamesFilter>[] = useMemo(
    () =>
      GAMES_FILTER_TABS.map((tab) =>
        tab.value === 'finished' ? { ...tab, disabled: !hasFinishedGames } : tab,
      ),
    [hasFinishedGames],
  );

  useEffect(() => {
    if (isRoundFinished) setFilter('all');
  }, [isRoundFinished]);

  const filteredGames = useMemo(() => {
    if (!source || filter === 'all') return source;

    return source.filter((g) => {
      const isFinished = g.winner !== undefined;
      return filter === 'finished' ? isFinished : !isFinished;
    });
  }, [source, filter]);

  const onGameClick = useCallback(
    (gameId: string) => {
      navigate({
        to: '/club/$clubId/tournament/$tournamentId/game/$gameId',
        params: { clubId, tournamentId, gameId },
        from: '/',
      });
    },
    [navigate, clubId, tournamentId],
  );

  if (!selectedRound) return null;

  return (
    <TournamentGames
      key={selectedRound?.roundId}
      theme={boardTheme}
      games={filteredGames}
      loading={!games}
      showToggle
      gamesHidden={gamesHidden}
      roundNumber={selectedRound?.roundNumber}
      roundsCount={visibleRounds?.length}
      roundRender={roundRender}
      roundsAreStages={isKnockout}
      onGameClick={onGameClick}
      onGamesToggle={onGamesToggle}
      onRoundClick={selectRound}
      renderShieldQuickPeek={renderShield}
      filters={
        !isRoundFinished ? (
          <Tabs tabs={filterTabs} flat value={filter} onChange={setFilter} noTabMinWidth />
        ) : undefined
      }
    />
  );
}

/*
    ____,-------------------------------,____
    \   |            Запчасти           |   /
    /___|-------------------------------|___\
*/

const LOCAL_STORAGE_KEY = 'hide_tournament_games';
const LOCAL_STORAGE = getLocalStorage();

function useGamesToggle() {
  const [gamesHidden, setGamesHidden] = useState(() => {
    return Boolean(LOCAL_STORAGE.getItem(LOCAL_STORAGE_KEY));
  });
  const onGamesToggle = useCallback(
    (newValue: boolean) => {
      setGamesHidden(newValue);
      LOCAL_STORAGE.setItem(LOCAL_STORAGE_KEY, newValue ? 'true' : '');
    },
    [setGamesHidden],
  );

  return { gamesHidden, onGamesToggle };
}

function renderShield(userId: string, className?: string) {
  return <PlayerShieldQuickPeekWidget className={className} userId={userId} />;
}
